/**
 * useRestaurants — React Query hook for restaurant data.
 * - Fetches from /api/restaurants with search params
 * - Client-side filtering (name, category) and sorting (distance, price)
 * - Stale-while-revalidate via React Query defaults
 * - localStorage persistence for 1-hour browser cache
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '../services/apiClient';
import { Restaurant, RestaurantListResponse, Category, SortBy } from '../types';

const CACHE_PREFIX = 'lf_restaurants_';
const CACHE_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour
const IS_TEST = import.meta.env.MODE === 'test';

/** Persist query result to localStorage */
function persistToStorage(key: string, data: RestaurantListResponse): void {
  if (IS_TEST) return;
  try {
    localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // Storage full or unavailable — non-critical
  }
}

/** Load cached data from localStorage if still fresh */
function loadFromStorage(key: string): RestaurantListResponse | undefined {
  if (IS_TEST) return undefined;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const { data, ts } = JSON.parse(raw) as { data: RestaurantListResponse; ts: number };
    if (Date.now() - ts > CACHE_MAX_AGE_MS) {
      localStorage.removeItem(key);
      return undefined;
    }
    return data;
  } catch {
    return undefined;
  }
}

interface UseRestaurantsParams {
  lat: number;
  lng: number;
  radius: 2 | 5 | 10 | 15 | 20;
  query?: string;
  categories?: Category[];
  sortBy?: SortBy;
  favoriteIds?: Set<string>;
  enabled?: boolean;
}

export function useRestaurants(params: UseRestaurantsParams) {
  const { lat, lng, radius, query, categories, sortBy = 'distance', favoriteIds, enabled = true } = params;

  const storageKey = `${CACHE_PREFIX}${lat}_${lng}_${radius}`;
  const cachedData = useMemo(() => loadFromStorage(storageKey), [storageKey]);

  const queryResult = useQuery({
    queryKey: ['restaurants', lat, lng, radius],
    queryFn: async () => {
      const result = await apiClient.get<RestaurantListResponse>(
        `/api/restaurants?lat=${lat}&lng=${lng}&radius=${radius}`
      );
      persistToStorage(storageKey, result);
      return result;
    },
    enabled,
    staleTime: 60 * 60 * 1000, // 1 hour — lunch data changes at most once per day
    gcTime: 2 * 60 * 60 * 1000, // 2 hours — keep in memory longer for navigation
    placeholderData: cachedData, // show cached while fetching, doesn't override fresh data
  });

  // Client-side filtering and sorting
  const filteredRestaurants = useMemo(() => {
    if (!queryResult.data?.restaurants) return [];

    let result = [...queryResult.data.restaurants];

    // Filter by name query
    if (query && query.length >= 1) {
      const q = query.toLowerCase();
      result = result.filter((r) => r.name.toLowerCase().includes(q));
    }

    // Filter by categories (OR: restaurant has at least one matching)
    if (categories && categories.length > 0) {
      result = result.filter((r) =>
        r.categories.some((c) => categories.includes(c))
      );
    }

    // Sort: favorites first (sorted by distance), then the rest by chosen sort
    result = sortRestaurants(result, sortBy, favoriteIds);

    return result;
  }, [queryResult.data, query, categories, sortBy, favoriteIds]);

  return {
    restaurants: filteredRestaurants,
    total: filteredRestaurants.length,
    searchPoint: queryResult.data?.searchPoint,
    radiusKm: queryResult.data?.radiusKm,
    cachedAt: queryResult.data?.cachedAt,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
}

/**
 * Sort restaurants by the given criterion.
 * Favorites always appear first (sorted by distance among themselves).
 * Non-favorites follow, sorted by the chosen criterion.
 * Null values go to the end within each group.
 */
function sortRestaurants(
  restaurants: Restaurant[],
  sortBy: SortBy,
  favoriteIds?: Set<string>
): Restaurant[] {
  return restaurants.sort((a, b) => {
    // Favorites first
    if (favoriteIds && favoriteIds.size > 0) {
      const aFav = favoriteIds.has(a.restaurantId);
      const bFav = favoriteIds.has(b.restaurantId);
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      // Both favorites: sort by distance
      if (aFav && bFav) {
        const aDist = a.distance ?? Infinity;
        const bDist = b.distance ?? Infinity;
        return aDist - bDist;
      }
    }

    // Non-favorites (or no favorites set): sort by chosen criterion
    if (sortBy === 'price') {
      const aPrice = a.price ?? Infinity;
      const bPrice = b.price ?? Infinity;
      return aPrice - bPrice;
    }
    // Default: distance
    const aDist = a.distance ?? Infinity;
    const bDist = b.distance ?? Infinity;
    return aDist - bDist;
  });
}
